import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { invalidatePageContent } from "../../lib/usePageContent";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { FieldInput } from "../../components/admin/FieldInput";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { ArrowLeft, ExternalLink, RotateCcw, CheckCircle2 } from "lucide-react";

// Mapping page-key → public route, used by the "Voir la page" button.
const PUBLIC_PREVIEW_PATH = {
  coming_soon: "/", // Coming Soon shows on / when maintenance is active
  header: "/",
  footer: "/",
  home: "/",
  plaidoyer: "/plaidoyer",
  about: "/a-propos",
  contact: "/contact",
  boutique: "/boutique",
  ressources: "/ressources",
};

export default function PageEditor() {
  const { page } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [originalValues, setOriginalValues] = useState({});
  const [pageLabel, setPageLabel] = useState(page);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => { if (user === false) navigate("/admin/login", { replace: true }); }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    setSaveError("");
    try {
      const [pagesResp, contentResp] = await Promise.all([
        api.get("/admin/pages"),
        api.get(`/admin/page-content/${page}`),
      ]);
      const meta = pagesResp.data.find((p) => p.page === page);
      if (meta) setPageLabel(meta.label);
      setFields(contentResp.data);
      const orig = {};
      contentResp.data.forEach((f) => { orig[f.key] = { value: f.value, visible: f.visible }; });
      setOriginalValues(orig);
    } catch (err) {
      setSaveError(formatApiError(err.response?.data?.detail) || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page]);

  const updateField = (key, newValue) => {
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, value: newValue } : f)));
    setSavedAt(null);
  };

  const toggleFieldVisible = (key, nextVisible) => {
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, visible: nextVisible } : f)));
    setSavedAt(null);
  };

  const dirtyKeys = useMemo(
    () => fields.filter((f) => {
      const orig = originalValues[f.key];
      if (!orig) return true;
      return f.value !== orig.value || f.visible !== orig.visible;
    }).map((f) => f.key),
    [fields, originalValues],
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const payload = {};
      dirtyKeys.forEach((k) => {
        const f = fields.find((x) => x.key === k);
        const orig = originalValues[k] || {};
        const entry = {};
        if (f.value !== orig.value) entry.value = f.value;
        if (f.visible !== orig.visible) entry.visible = f.visible;
        if (Object.keys(entry).length) payload[k] = entry;
      });
      const { data } = await api.post(`/admin/page-content/${page}`, payload);
      const newOriginal = { ...originalValues };
      fields.forEach((f) => { newOriginal[f.key] = { value: f.value, visible: f.visible }; });
      setOriginalValues(newOriginal);
      setSavedAt(data.updated_at);
      invalidatePageContent(page); // bust the public hook cache
    } catch (err) {
      setSaveError(formatApiError(err.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await api.post(`/admin/page-content/${page}/reset`);
      invalidatePageContent(page);
      setResetOpen(false);
      await load();
    } catch (err) {
      setSaveError(formatApiError(err.response?.data?.detail) || "Échec de la réinitialisation");
    }
  };

  if (!user) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  const previewPath = PUBLIC_PREVIEW_PATH[page] || "/";

  return (
    <div className="min-h-screen bg-[#FAF8F3] pb-32" data-testid="page-editor">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/admin/pages" className="flex items-center gap-2 text-[#1A1A18] hover:text-[#4A7A3D]" data-testid="editor-back">
              <ArrowLeft size={16} /> <Logo size="sm" />
            </Link>
            <span className="font-mono-label text-[#4A7A3D] hidden md:inline truncate">CMS · {pageLabel.toUpperCase()}</span>
          </div>
          <div className="flex items-center gap-3">
            <a href={previewPath} target="_blank" rel="noopener noreferrer" className="font-mono-label text-[#1A1A18] hover:text-[#4A7A3D] flex items-center gap-2" data-testid="editor-preview">
              <ExternalLink size={14} /> <span className="hidden sm:inline">Voir la page</span>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-display text-4xl md:text-5xl mb-3">{pageLabel}</h1>
        <p className="text-[#4B4B48] mb-10">{fields.length} champ{fields.length > 1 ? "s" : ""} éditable{fields.length > 1 ? "s" : ""}.</p>

        {loading ? (
          <p className="font-mono-label">CHARGEMENT…</p>
        ) : (
          <div className="space-y-7">
            {fields.map((f) => (
              <div key={f.key} className="border-l-2 border-[#E0D8CE] pl-5 py-1" data-testid={`field-block-${f.key}`}>
                <label htmlFor={`field-input-${f.key}`} className="font-mono-label text-[#1A1A18] block mb-1">
                  {f.label}
                  {dirtyKeys.includes(f.key) && (
                    <span className="ml-2 text-[#C4944A]" data-testid={`field-dirty-${f.key}`}>● modifié</span>
                  )}
                </label>
                <p className="font-mono-label text-[10px] text-[#4B4B48] mb-2">
                  Clé · {f.key} · type · {f.type}
                  {f.updated_by && ` · dernière modif par ${f.updated_by}`}
                </p>
                <FieldInput
                  field={f}
                  onChange={(v) => updateField(f.key, v)}
                  onToggleVisible={(vis) => toggleFieldVisible(f.key, vis)}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Sticky save bar */}
      {!loading && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#1A1A18] text-[#FAF8F3] border-t-2 border-[#1A1A18] z-40" data-testid="editor-save-bar">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="font-mono-label flex flex-wrap items-center gap-2">
              {dirtyKeys.length === 0 && savedAt && (
                <span className="text-[#8FAF82] flex items-center gap-2" data-testid="editor-saved">
                  <CheckCircle2 size={14} /> Modifications enregistrées
                </span>
              )}
              {dirtyKeys.length > 0 && (
                <span className="text-[#C4944A]" data-testid="editor-dirty-count">
                  ● {dirtyKeys.length} modification{dirtyKeys.length > 1 ? "s" : ""} non enregistrée{dirtyKeys.length > 1 ? "s" : ""}
                </span>
              )}
              {dirtyKeys.length === 0 && !savedAt && (
                <span className="text-[#FAF8F3]/60">Aucune modification en cours</span>
              )}
              {saveError && <span className="text-red-300">{saveError}</span>}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setResetOpen(true)}
                className="min-h-[44px] border border-[#FAF8F3]/30 px-4 py-2 font-mono-label hover:border-[#C4944A] hover:text-[#C4944A] flex items-center justify-center gap-2"
                data-testid="editor-reset"
              >
                <RotateCcw size={14} /> Réinitialiser
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || dirtyKeys.length === 0}
                className="min-h-[44px] bg-[#4A7A3D] text-[#FAF8F3] px-6 py-2 font-mono-label hover:bg-[#3E6633] disabled:bg-[#FAF8F3]/20 disabled:cursor-not-allowed"
                data-testid="editor-save"
              >
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="editor-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Réinitialiser cette page ?</DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              Tous les champs de cette page reviendront à leur valeur par défaut. Tes modifications sauvegardées seront perdues.
              Les autres pages ne sont pas affectées.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:gap-2">
            <button onClick={() => setResetOpen(false)} className="min-h-[44px] border-2 border-[#1A1A18] px-5 py-2 font-mono-label hover:bg-[#F5EFEB]" data-testid="editor-reset-cancel">
              Annuler
            </button>
            <button onClick={handleReset} className="min-h-[44px] bg-red-700 text-[#FAF8F3] px-5 py-2 font-mono-label hover:bg-red-800" data-testid="editor-reset-confirm">
              Oui, réinitialiser
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
